/**
 * HomePage.js - Granullo Public Homepage
 *
 * GRANULLO_HOMEPAGE_NARRATIVE_COMPRESSION_AND_PILLAR_IA_LOCK_V1
 * 
 * 8-SECTION CANONICAL SPINE (FOUNDER LOCKED):
 * 1. Hero — core proposition + CTAs
 * 2. Why Now — autonomous money, AI agents, misuse risk
 * 3. Historical Timeline — structural failures proof
 * 4. Structural Theorem — what changes + objections
 * 5. Start From Your World — Discover + Create
 * 6. The Proof Loop — Prevent → Twin → Memory → Simulate → Improve
 * 7. Memory + Intelligence — institutional memory doctrine
 * 8. Trust + CTA — Merged flagship chapter (graph + theorem + CTA)
 *
 * No duplicate narrative jobs across sections.
 * No repeated emotional peaks.
 * Pillar depth lives at /platform, /use-cases, /intelligence.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { GranulloIntelligenceProvider, useGranulloIntelligence, COLORS as SYS_C } from '../contexts/GranulloIntelligenceContext';
import { ThemeToggle } from '../components/theme/ThemeToggle';
import { InstitutionalNavigation, InstitutionalMobileNavigation, InstitutionalMobileMenuButton } from '../components/navigation/InstitutionalNavigation';
import { chapterLine, chapterThesis } from '../lib/motionGrammar';
import EditorialSpine from '../components/homepage/EditorialSpine';
import MinimalHero from '../components/homepage/MinimalHero';
import HistoricalTimeline from '../components/homepage/HistoricalTimeline';
import PlayTeaser from '../components/homepage/PlayTeaser';
import OutcomeParadoxSection from '../components/homepage/OutcomeParadoxSection';
import CapitalCoordinatesChapter from '../components/homepage/CapitalCoordinatesChapter';
import PromiseArrivalSection from '../components/homepage/PromiseArrivalSection';
import GranulloBridgeSection from '../components/homepage/GranulloBridgeSection';
import HomepageConversionEngine from '../components/homepage/HomepageConversionEngine';
import OutcomeUpliftBars from '../components/homepage/OutcomeUpliftBars';
import HeroTwinBridge from '../components/homepage/HeroTwinBridge';
import TrustCompoundingGraph from '../components/homepage/TrustCompoundingGraph';
import MeetGranulloIntelligence from '../components/homepage/MeetGranulloIntelligence';
import PublicDiscoverSection from '../components/homepage/PublicDiscoverSection';
import ProgramsTeaser from '../components/homepage/ProgramsTeaser';
import TrustEdgeCTA from '../components/homepage/TrustEdgeCTA';
import { AccordionSpecies } from '../components/homepage/AccordionSpecies';
import ChapterEyebrow from '../components/homepage/ChapterEyebrow';
import PatternLibrarySection from '../components/homepage/PatternLibrarySection';
// FutureMoneySection merged into PromiseArrivalSection (interactive scale grid)
import TradeCorridorMap from '../components/homepage/TradeCorridorMap';
import { LayoutDashboard } from 'lucide-react';

/**
 * GlassNavHeader — Simplified premium navigation
 * 
 * Cleaned per FOUNDER DIRECTIVE:
 * - One clear CTA: "Try Granullo"
 * - Clean, calm, readable
 * - No system telemetry
 * - No competing actions
 */
const GlassNavHeader = ({ user, mobileMenuOpen, setMobileMenuOpen }) => {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className="ed-nav fixed top-0 left-0 right-0 z-50"
      style={{ height: isScrolled ? '52px' : '56px', transition: 'height 200ms ease' }}
      data-testid="glass-nav-header"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* LEFT: Logo + Primary Nav */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div
                className="flex items-center justify-center transition-all duration-200"
                style={{
                  width: isScrolled ? 24 : 28,
                  height: isScrolled ? 24 : 28,
                  borderRadius: 4,
                  background: 'var(--shell-text)',
                }}
              >
                <span
                  className="font-semibold"
                  style={{
                    color: 'var(--shell-bg)',
                    fontSize: isScrolled ? 10 : 12,
                  }}
                >G</span>
              </div>
              <span
                className="tracking-[-0.01em]"
                style={{
                  color: 'var(--shell-text)',
                  fontWeight: 500,
                  fontSize: isScrolled ? 14 : 15,
                  transition: 'font-size 200ms ease',
                }}
              >Granullo</span>
            </Link>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center">
              <InstitutionalNavigation standalone={false} />
            </div>
          </div>

          {/* RIGHT: Auth Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle compact />

            {user ? (
              <Link
                to="/dashboard"
                className="hidden sm:inline-flex items-center gap-2 text-[13px] transition-colors"
                style={{ color: 'var(--shell-text-muted)' }}
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-flex items-center text-[13px] transition-colors px-2 py-1.5"
                  style={{ color: 'var(--shell-text-muted)' }}
                  data-testid="nav-sign-in-btn"
                >
                  Sign in
                </Link>
              </>
            )}

            {/* Mobile Menu Button */}
            <InstitutionalMobileMenuButton
              isOpen={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            />
          </div>
        </div>
      </div>
    </nav>
  );
};

/**
 * SystemPresence — Persistent intelligence field
 * Faint, always present, reacts to system state.
 * The system is always observing. Always evaluating.
 */
const PRESENCE_NODES = Array.from({ length: 5 }, (_, i) => ({
  left: `${15 + i * 18}%`,
  top: `${20 + (i % 3) * 30}%`,
  delay: i * 0.6,
}));

const SystemPresence = () => {
  const { totalTests, intensity, consecutiveBlocked } = useGranulloIntelligence();
  const baseOpacity = 0.015 + Math.min(0.02, totalTests * 0.003);
  const pulseColor = consecutiveBlocked > 0 ? SYS_C.blocked : SYS_C.system;

  return (
    <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true" data-testid="system-presence">
      {PRESENCE_NODES.map((n, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            left: n.left, top: n.top,
            width: 3, height: 3,
            background: `${pulseColor}${Math.round(baseOpacity * 255).toString(16).padStart(2, '0')}`,
          }}
          animate={{
            opacity: [baseOpacity * 8, baseOpacity * 16, baseOpacity * 8],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 4 + i * 0.5, repeat: Infinity, delay: n.delay, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
};

/* ═══ STRUCTURAL OBJECTION LAYER — System-State Grammar ═══ */
const FOUNDER_OBJECTIONS = [
  {
    question: 'What about the rails we already use?',
    answer: `Banks, processors, card networks continue.
Structural governance sits above them.

Movement without declared proof
does not occur.`,
  },
  {
    question: 'Do we have to rip out banks or fintech partners?',
    answer: `Banks settle.
Fintechs distribute.
Governance governs.

Invalid movement does not pass.
Valid movement moves unchanged.`,
  },
  {
    question: 'What happens when AI agents spend money on their own?',
    answer: `Autonomous action operates inside structural boundaries.
Boundaries are defined before execution.

Every disbursement generates its own proof.
Movement outside program rules does not occur.`,
  },
  {
    question: 'Does this work across countries and corridors?',
    answer: `Structural governance is jurisdiction-agnostic.
Rule engine and proof architecture operate identically across currencies, corridors, regulators.

Movement that violates local law
does not occur.`,
  },
  {
    question: 'Who owns the rules and the evidence?',
    answer: `The program defines the rules.
The system generates the evidence structurally.

Every proof event, every twin, every consequence record
belongs to the program and its participants.

Nothing is extracted.
Nothing is inferred.`,
  },
  {
    question: 'What does this prove that auditors cannot?',
    answer: `Auditors verify after movement.
Invalid movement does not occur to be audited.

Every payment carries its own proof at execution.
Sampling does not apply.
Quarterly windows do not apply.

The incorrect transaction does not exist.`,
  },
  {
    question: 'How fast can a pilot go live?',
    answer: `Single-program pilot: operational within days.
Rule behavior witnesses in simulation before real movement occurs.

Multi-program deployments with custom compliance surfaces
complete in weeks.`,
  },
];

const StructuralObjectionLayer = () => (
  <section className="py-20 sm:py-28 px-6 lg:px-8" data-testid="structural-objection-section">
    <div className="max-w-[720px] mx-auto">
      {/* Section header — Corporate Rebels editorial */}
      <div className="mb-12 sm:mb-16">
        <p
          className="text-[11px] sm:text-[12px] font-semibold tracking-[0.12em] uppercase mb-4"
          style={{ color: 'var(--text-muted, #6B6B6B)' }}
          data-testid="objection-layer-label"
        >
          Structural objections
        </p>
        <h2
          className="text-[28px] sm:text-[32px] font-semibold leading-[1.15] tracking-[-0.02em] mb-3"
          style={{ color: 'var(--text-primary, #0A0A0A)' }}
          data-testid="objection-layer-heading"
        >
          Before movement,{' '}
          <span
            style={{
              fontFamily: '"Permanent Marker", cursive',
              color: 'var(--g-editorial-red, #CD2200)',
              fontSize: '0.92em',
            }}
          >
            read this
          </span>
        </h2>
        <p
          className="text-[14px] sm:text-[15px] leading-[1.75]"
          style={{ color: 'var(--text-secondary, #555555)', maxWidth: 520, whiteSpace: 'pre-line' }}
        >
          {`What institutions ask before they stay.
What the architecture answers.`}
        </p>
      </div>

      {/* AccordionSpecies — Corporate Rebels grammar */}
      <AccordionSpecies
        items={FOUNDER_OBJECTIONS}
        defaultOpen={0}
        className=""
      />
    </div>
  </section>
);

const HomePage = () => {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <GranulloIntelligenceProvider>
      <div className="overflow-x-hidden bg-background text-foreground relative">
        <SystemPresence />
        {/* Editorial Spine — right-side narrative rail */}
        <EditorialSpine />

        {/* Navigation */}
        <GlassNavHeader
          user={user}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <InstitutionalMobileNavigation
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        {/* Spacer for fixed nav */}
        <div className="h-14" />

        {/* ═══ SECTION 1 — HERO ═══ */}
        <div id="spine-hero" className="g-scene-bright relative overflow-hidden">
          <MinimalHero />
        </div>

        {/* ═══ SECTION 2 — CSR / IMPACT: "NEED HAS AN ADDRESS" (Flagship opener) ═══ */}
        <div id="spine-capital-coordinates" className="g-scene-white">
          <ChapterEyebrow number="01" title="CSR / Impact" />
          <CapitalCoordinatesChapter />
        </div>

        {/* ── Chapter Transition — from CSR/impact into the universal thesis ── */}
        <div className="g-chapter-break g-scene-bright relative overflow-hidden">
          <motion.div className="g-chapter-line" {...chapterLine} />
          <motion.p className="g-chapter-thesis" {...chapterThesis}>
            Impact capital is the first address we drew. Every flow of money needs one.
          </motion.p>
        </div>

        {/* ═══ SECTION 3 — MONEY ≠ MISUSE: OUTCOME PARADOX (the dissolve) ═══ */}
        <div id="spine-paradox">
          <ChapterEyebrow number="02" title="Money ≠ Misuse" />
          <OutcomeParadoxSection />
        </div>

        {/* ═══ SECTION 4 — MONEY ≠ MISUSE across scale: PROMISE → ARRIVAL (26 scenarios) ═══ */}
        <div id="spine-now">
          <ChapterEyebrow number="03" title="Scale of Misuse" />
          <PromiseArrivalSection />
        </div>

        {/* ── Chapter Transition — 4 centuries ── */}
        <div className="g-chapter-break g-scene-bright relative overflow-hidden">
          <motion.div className="g-chapter-line" {...chapterLine} />
          <motion.p className="g-chapter-thesis" {...chapterThesis}>
            Four centuries of movement without proof. The evidence is structural.
          </motion.p>
        </div>

        {/* ═══ SECTION 5 — HISTORICAL TIMELINE (collapse proof) ═══ */}
        <div id="spine-history" className="g-scene-tinted relative overflow-hidden">
          <ChapterEyebrow number="04" title="Historical Collapse" />
          <HistoricalTimeline />
        </div>

        {/* ═══ SECTION 6 — TRADE CORRIDOR INTELLIGENCE MAP (commerce layer) ═══ */}
        <div id="spine-corridors">
          <ChapterEyebrow number="05" title="Global Commerce" />
          <TradeCorridorMap />
        </div>

        {/* ═══ SECTION 7 — GRANULLO PLAY (interactive/engagement layer) ═══ */}
        <div id="spine-play">
          <ChapterEyebrow number="06" title="Play the Game" />
          <PlayTeaser />
        </div>

        {/* ═══ SECTION 8 — STRUCTURAL THEOREM ═══ */}
        <div id="spine-theorem" className="g-scene-white">
          <ChapterEyebrow number="07" title="The Theorem" />
          <GranulloBridgeSection />
          <StructuralObjectionLayer />
        </div>

        {/* ═══ SECTION 9 — START FROM YOUR WORLD · Pattern Library (transaction allocation doctrine) ═══ */}
        <div id="spine-discover" className="g-scene-tinted">
          <ChapterEyebrow number="08" title="Start From Your World" />
          <PatternLibrarySection />
        </div>

        {/* ═══ SECTION 10 — THE PROOF LOOP ═══ */}
        <div id="spine-proof">
          <ChapterEyebrow number="09" title="The Proof Loop" />
          {/* SIGNATURE_SECTION_DO_NOT_DILUTE */}
          <OutcomeUpliftBars />
          <HeroTwinBridge />
        </div>

        {/* ═══ SECTION 11 — MEMORY + INTELLIGENCE (final content section) ═══ */}
        <div id="spine-trust" className="relative">
          <div className="g-scene-tinted">
            <ChapterEyebrow number="10" title="Memory + Intelligence" />
            <MeetGranulloIntelligence />
          </div>
        </div>

        {/* ═══ SECTION 12 — ACTIVATION · Discover programs for YOUR world ═══ */}
        <div id="spine-activation" className="g-scene-white">
          <ChapterEyebrow number="11" title="Start With Your URL" />
          <PublicDiscoverSection />
        </div>

        {/* ═══ SECTION 13 — PROGRAMS TEASER · Four deployable pilot packs ═══ */}
        <div id="spine-programs" className="g-scene-white">
          <ChapterEyebrow number="12" title="Programs" />
          <ProgramsTeaser />
        </div>
      </div>
    </GranulloIntelligenceProvider>
  );
};

export default HomePage;
